<template>
  <Head title="Gallery" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Gallery', href: route('gallery.index') },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <!-- Form Section -->
          <div
            class="order-2 col-span-1 space-y-6 lg:order-1"
            v-if="$page.props.auth.user && $page.props.can.galeri_create"
          >
            <div class="bg-white border border-gray-200">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-rose-800 to-yellow-600 sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-images sm:mr-3"></i>
                  {{ form.id ? "Edit Gallery" : "Tambah Gallery" }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <form class="space-y-4" @submit.prevent="submit">
                  <!-- Image Preview -->
                  <div v-if="form.imagePreview.length">
                    <label class="block mb-2 text-sm font-medium text-gray-900">
                      Preview Gambar
                    </label>
                    <div class="flex flex-wrap gap-2">
                      <img
                        v-for="(src, index) in form.imagePreview"
                        :key="index"
                        :src="src"
                        class="object-cover w-16 h-16 border border-gray-200 rounded-lg shadow-sm"
                      />
                    </div>
                  </div>

                  <!-- Title -->
                  <div>
                    <label
                      for="title"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Judul Gallery
                    </label>
                    <input
                      id="title"
                      type="text"
                      v-model="form.title"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      placeholder="Masukkan judul gallery"
                    />
                    <InputError :message="form.errors.title" class="mt-2" />
                  </div>

                  <!-- Description -->
                  <div>
                    <label
                      for="description"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Deskripsi
                    </label>
                    <textarea
                      id="description"
                      v-model="form.description"
                      rows="3"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
                      placeholder="Masukkan deskripsi gallery"
                    ></textarea>
                    <InputError
                      :message="form.errors.description"
                      class="mt-2"
                    />
                  </div>

                  <!-- Images -->
                  <div>
                    <label
                      for="image"
                      class="block mb-2 text-sm font-medium text-gray-900"
                    >
                      Gambar Gallery
                    </label>
                    <input
                      id="image"
                      type="file"
                      ref="fileInput"
                      accept="image/*"
                      multiple
                      @change="handleFileChange"
                      class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200 file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    />
                    <p class="mt-1 text-xs text-gray-500">
                      Pilih beberapa gambar (JPG, PNG, JPEG)
                    </p>
                    <progress
                      v-if="form.progress"
                      :value="form.progress.percentage"
                      max="100"
                      class="w-full h-2 mt-2 bg-gray-200 rounded-lg"
                    >
                      {{ form.progress.percentage }}%
                    </progress>
                    <InputError :message="form.errors.image" class="mt-2" />
                  </div>

                  <!-- Submit Button -->
                  <div class="pt-4">
                    <button
                      type="submit"
                      :disabled="form.processing"
                      class="w-full px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                    >
                      <i class="mr-2 fa-solid fa-floppy-disk"></i>
                      {{ form.processing ? "Menyimpan..." : "Simpan Gallery" }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Table Section -->
          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Daftar Gallery
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Kelola koleksi gambar dan foto gallery
                  </p>
                </div>
                <div class="mt-2 text-sm text-gray-500 sm:mt-0">
                  Total: {{ totalItems }} gallery
                </div>
              </div>
            </div>

            <!-- Search Input -->
            <div class="relative mb-4">
              <div
                class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none"
              >
                <MagnifyingGlassVue class="w-4 h-4 text-gray-400" />
              </div>
              <input
                v-model="search"
                type="text"
                autocomplete="off"
                placeholder="Search gallery..."
                id="search"
                class="block w-full px-3 py-2.5 pl-10 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors duration-200"
              />
            </div>

            <!-- Table -->
            <div
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[150px]"
                      >
                        Gallery
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[200px]"
                      >
                        Deskripsi
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 min-w-[120px]"
                      >
                        Gambar
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[100px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(galeri, index) in paginatedGalleries"
                      :key="galeri.id"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                      </td>
                      <td class="px-3 py-4 text-sm">
                        <div class="flex items-center">
                          <div class="flex-shrink-0 w-12 h-12 mr-3">
                            <img
                              v-if="galeri.image_path"
                              :src="
                                Array.isArray(galeri.image_path)
                                  ? galeri.image_path[0]
                                  : galeri.image_path
                              "
                              :alt="galeri.title"
                              class="object-cover w-12 h-12 border border-gray-200 rounded-lg shadow-sm"
                            />
                            <div
                              v-else
                              class="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg"
                            >
                              <i class="text-gray-400 fa-solid fa-image"></i>
                            </div>
                          </div>
                          <div class="flex-grow min-w-0">
                            <span
                              class="block font-medium text-gray-900 truncate"
                            >
                              {{ galeri.title }}
                            </span>
                            <div class="text-xs text-gray-500">
                              {{
                                Array.isArray(galeri.image_path)
                                  ? galeri.image_path.length
                                  : galeri.image_path
                                  ? 1
                                  : 0
                              }}
                              foto
                            </div>
                            <!-- Mobile view: Show description -->
                            <div
                              class="mt-1 text-xs text-gray-500 md:hidden line-clamp-2"
                            >
                              {{ galeri.description || "Tidak ada deskripsi" }}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 md:table-cell"
                      >
                        <div class="max-w-xs line-clamp-3">
                          {{ galeri.description || "Tidak ada deskripsi" }}
                        </div>
                      </td>
                      <td class="px-3 py-4 text-sm text-gray-500">
                        <div class="flex items-center space-x-2">
                          <span
                            :class="
                              (Array.isArray(galeri.image_path)
                                ? galeri.image_path.length
                                : galeri.image_path
                                ? 1
                                : 0) > 0
                                ? 'inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full'
                                : 'inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full'
                            "
                          >
                            <i class="mr-1 fa-solid fa-images"></i>
                            {{
                              Array.isArray(galeri.image_path)
                                ? galeri.image_path.length
                                : galeri.image_path
                                ? 1
                                : 0
                            }}
                          </span>
                        </div>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Action Buttons -->
                          <Link
                            v-if="$page.props.can.galeri_show"
                            :href="route('gallery.show', galeri.idgallery)"
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 hover:text-blue-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="View"
                          >
                            <i class="w-3 h-3 fa-solid fa-eye"></i>
                            <span class="hidden ml-1 sm:inline">View</span>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Pagination -->
            <div class="flex items-center justify-between mt-6">
              <div class="flex items-center text-sm text-gray-500">
                Menampilkan {{ startIndex }} sampai {{ endIndex }} dari
                {{ totalItems }} data
              </div>
              <div class="flex items-center space-x-2">
                <button
                  @click="previousPage"
                  :disabled="currentPage === 1"
                  class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 transition-colors duration-200 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span
                  v-for="page in visiblePages"
                  :key="page"
                  class="inline-flex items-center px-3 py-2 text-sm font-medium transition-colors duration-200 border rounded-md cursor-pointer"
                  :class="
                    page === currentPage
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                  "
                  @click="goToPage(page)"
                >
                  {{ page }}
                </span>
                <button
                  @click="nextPage"
                  :disabled="currentPage === totalPages"
                  class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 transition-colors duration-200 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>
<script setup>
import { Head, Link, useForm } from "@inertiajs/vue3";
import { ref, computed, watch } from "vue";
import Container from "@/Layouts/Container.vue";
import InputError from "@/Components/InputError.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import MagnifyingGlassVue from "@/Components/MagnifyingGlass.vue";
import Swal from "sweetalert2";

const props = defineProps({
  galleries: {
    type: Object,
    required: true,
  },
});

// Search and pagination
const search = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);

// Convert galleries.data to a simple array for client-side operations
const allGalleries = computed(() => {
  return props.galleries.data || [];
});

const filteredGalleries = computed(() => {
  if (!search.value) return allGalleries.value;
  return allGalleries.value.filter(
    (gallery) =>
      gallery.title.toLowerCase().includes(search.value.toLowerCase()) ||
      gallery.description.toLowerCase().includes(search.value.toLowerCase())
  );
});

// Pagination computed properties
const totalItems = computed(() => filteredGalleries.value.length);
const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage.value)
);

const paginatedGalleries = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return filteredGalleries.value.slice(start, end);
});

const startIndex = computed(() => {
  if (totalItems.value === 0) return 0;
  return (currentPage.value - 1) * itemsPerPage.value + 1;
});

const endIndex = computed(() => {
  const end = currentPage.value * itemsPerPage.value;
  return end > totalItems.value ? totalItems.value : end;
});

const visiblePages = computed(() => {
  const pages = [];
  const start = Math.max(1, currentPage.value - 2);
  const end = Math.min(totalPages.value, currentPage.value + 2);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

// Pagination methods
const goToPage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

// Reset to page 1 when search changes
watch(search, () => {
  currentPage.value = 1;
});

// Form handling
const form = useForm({
  id: null,
  title: "",
  description: "",
  image: [],
  imagePreview: [], // untuk tampung URL preview
});

const handleFileChange = (e) => {
  const files = Array.from(e.target.files);
  form.image = files;
  form.imagePreview = files.map((file) => URL.createObjectURL(file));
};

const fileInput = ref(null);

const submit = () => {
  form.post(route("gallery.store"), {
    forceFormData: true,
    onSuccess: () => {
      Swal.fire({
        title: "Berhasil!",
        text: "Gallery berhasil disimpan",
        icon: "success",
        confirmButtonText: "OK",
      });
      form.reset();
      form.image = [];
      form.imagePreview = [];
      if (fileInput.value) {
        fileInput.value.value = null;
      }
    },
    onError: () => {
      Swal.fire({
        title: "Error!",
        text: "Gagal menyimpan gallery",
        icon: "error",
        confirmButtonText: "OK",
      });
    },
    preserveScroll: true,
  });
};
</script>
